import * as React from "react"

import { cn } from "@/helpers/classNames"

interface TableCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  colSpan?: number // `colSpan` seems to be missing on React.HTMLAttributes<HTMLTableCellElement>
}

function Table({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLTableElement>
} & React.HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn(
          "w-full border-separate border-spacing-0 whitespace-nowrap",
          className,
        )}
        {...props}
      />
    </div>
  )
}

function TableHeader({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLTableSectionElement>
} & React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
  )
}

function TableBody({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLTableSectionElement>
} & React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableRow({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLTableRowElement>
} & React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      ref={ref}
      className={cn(
        `
          border-b transition-colors
          hover:bg-muted/50
          data-[state=selected]:bg-muted
        `,
        className,
      )}
      {...props}
    />
  )
}

function TableHead({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLTableCellElement>
} & React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      ref={ref}
      className={cn(
        `
          border-b border-neutral-300 bg-neutral-100 px-4 py-2 text-left text-sm
          text-neutral-600
        `,
        className,
      )}
      {...props}
    />
  )
}

export function TableSubHead({ className, ...props }: TableCellProps) {
  return (
    <th
      {...props}
      className={cn([
        `
          border-b border-neutral-300 bg-neutral-200 px-4 py-2 text-left text-xs
          text-neutral-600
        `,
        className,
      ])}
      data-unmask
    />
  )
}

function TableCell({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLTableCellElement>
} & React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      ref={ref}
      className={cn(
        `
          border-b border-neutral-300 bg-neutral-100 px-4 py-2 text-sm
          text-neutral-700
        `,
        className,
      )}
      {...props}
    />
  )
}

export { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
